<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="primary--text">
        Inscribirse en Torneo
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <!-- Información del torneo -->
        <div v-if="tournament" class="mb-6 pa-4 grey lighten-4 rounded">
          <h3 class="font-weight-bold mb-2">{{ tournament.name }}</h3>
          <div class="text-caption grey--text mb-2">
            <v-icon small>mdi-calendar</v-icon>
            {{ formatDate(tournament.start_date) }}
          </div>
          <div class="text-caption grey--text">
            <v-icon small>mdi-golf</v-icon>
            {{ courseDisplay }}
          </div>
        </div>

        <v-form ref="form" @submit.prevent="handleSubmit">
          <!-- Tipo de participante (Solo RP) -->
          <div v-if="isRp" class="mb-4">
            <label class="font-weight-bold text-caption mb-2 d-block">
              ¿Quién se inscribe?
            </label>
            <v-radio-group v-model="form.type" @change="resetParticipantData" hide-details class="mt-0">
              <v-radio label="Usuario" value="user"></v-radio>
              <v-radio label="Invitado Externo" value="guest"></v-radio>
            </v-radio-group>
          </div>

          <!-- Usuario del Sistema o CCSLP (Solo RP) -->
          <v-combobox
            v-if="form.type === 'user'"
            v-model="selectedParticipant"
            :items="combinedParticipants"
            :loading="loadingUsers || loadingCCSLPUsers"
            :search-input.sync="participantSearch"
            item-text="full_name"
            item-value="id"
            label="Seleccionar Usuario"
            outlined
            dense
            required
            :rules="[rules.required]"
            clearable
            prepend-inner-icon="mdi-account-search"
            hide-details="auto"
            return-object
            :filter="filterParticipants"
            class="mb-4 mt-4"
            no-data-text="No se encontraron usuarios"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.displaySubtitle }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-combobox>

          <!-- Invitado Externo (Solo RP) -->
          <template v-if="form.type === 'guest'">
            <v-text-field
              v-model="form.guest_name"
              label="Nombre completo"
              outlined
              dense
              required
              :rules="[rules.required]"
              class="mb-4 mt-4"
              placeholder="Ej: Pedro García Rodríguez"
              prepend-inner-icon="mdi-account"
            ></v-text-field>

            <v-text-field
              v-model="form.guest_email"
              label="Email"
              type="email"
              outlined
              dense
              required
              :rules="[rules.required, rules.email]"
              class="mb-4"
              placeholder="Ej: pedro@example.com"
              prepend-inner-icon="mdi-email"
            ></v-text-field>

            <v-text-field
              v-model.number="form.guest_handicap"
              label="Handicap (Opcional)"
              type="number"
              outlined
              dense
              min="0"
              max="54"
              class="mb-4"
              prepend-inner-icon="mdi-golf"
            ></v-text-field>
          </template>

          <!-- Validación de handicap del torneo -->
          <v-alert
            v-if="handicapWarning"
            type="warning"
            dense
            text
            class="mb-4"
          >
            {{ handicapWarning }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Cancelar</v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!canRegister"
          @click="handleSubmit"
        >
          Inscribir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters } from 'vuex'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'RegisterDialog',
  props: {
    tournament: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    isRp: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: true,
      form: {
        type: 'user',
        guest_name: '',
        guest_email: '',
        guest_handicap: 0
      },
      selectedParticipant: null,
      allUsers: [],
      ccslpUsers: [],
      loadingUsers: false,
      loadingCCSLPUsers: false,
      participantSearch: null,
      rules: {
        required: (v) => !!v || 'Campo requerido',
        email: (v) => /.+@.+\..+/.test(v) || 'Email no válido',
        handicapRange: (v) =>
          (v >= 0 && v <= 54) || 'Handicap debe estar entre 0 y 54'
      }
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    courseDisplay () {
      if (this.tournament?.courses_detail?.length) {
        return this.tournament.courses_detail.map(c => c.name).join(', ')
      }
      return this.tournament?.course || ''
    },
    combinedParticipants () {
      const users = this.allUsers.map(u => ({
        ...u,
        type: 'user'
      }))
      const ccslps = this.ccslpUsers.map(c => ({
        ...c,
        type: 'ccslp'
      }))
      return [...users, ...ccslps]
    },
    canRegister () {
      if (this.form.type === 'user') return !!this.selectedParticipant?.id
      if (this.form.type === 'guest') {
        const emailValid = /.+@.+\..+/.test(this.form.guest_email)
        return !!this.form.guest_name && emailValid
      }
      return false
    },
    handicapWarning () {
      let handicap = 0

      if (this.form.type === 'guest') {
        handicap = this.form.guest_handicap
      } else {
        return ''
      }

      if (this.tournament.handicap_min >= 0 && handicap < this.tournament.handicap_min) {
        return `Handicap por debajo del mínimo requerido (${this.tournament.handicap_min})`
      }
      if (handicap > this.tournament.handicap_max) {
        return `Handicap por encima del máximo permitido (${this.tournament.handicap_max})`
      }
      return ''
    }
  },
  mounted () {
    if (this.isRp) {
      this.loadUsers()
      this.loadCCSLPUsers()
    }
  },
  methods: {
    getIdHousingUnit () {
      return localStorage.getItem('idHousingUnit') || store.getters.getProfile?.idHousingUnit
    },
    async loadUsers () {
      this.loadingUsers = true
      try {
        const idHousingUnit = this.getIdHousingUnit()
        const config = {
          method: 'get',
          url: `${API_BASE_URL}/residents/all`,
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': 'es',
            'authorization': 'Token ' + store.getters.getToken
          },
          params: idHousingUnit ? { housing_unit: idHousingUnit } : {}
        }
        const response = await axios(config)
        const residents = response.data.residents || response.data || []
        this.allUsers = residents.map(resident => ({
          id: resident.idUser?.id,
          full_name: `${resident.idUser?.first_name || ''} ${resident.idUser?.last_name || ''}`,
          email: resident.idUser?.email,
          displaySubtitle: resident.idUser?.email || ''
        }))
      } catch (error) {
        console.error('Error fetching users:', error)
        this.allUsers = []
      } finally {
        this.loadingUsers = false
      }
    },
    async loadCCSLPUsers () {
      const idHousingUnit = this.getIdHousingUnit()
      if (idHousingUnit !== 'a58d8679-e2ae-4055-a243-c63d438f19eb') {
        this.ccslpUsers = []
        return
      }

      this.loadingCCSLPUsers = true
      try {
        const batchSize = 500
        let offset = 0
        let allCCSLPUsers = []
        let hasMore = true

        while (hasMore) {
          const config = {
            method: 'get',
            url: `${API_BASE_URL}/ccslp/users/?limit=${batchSize}&offset=${offset}`,
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'Token ' + store.getters.getToken
            }
          }
          const response = await axios(config)
          const batch = response.data.users || []
          allCCSLPUsers = allCCSLPUsers.concat(batch)
          hasMore = batch.length === batchSize
          offset += batchSize
        }

        this.ccslpUsers = allCCSLPUsers
          .filter(u => !u.is_moroso)
          .map(u => ({
            id: u.id,
            full_name: u.name,
            displaySubtitle: u.socio ? `Socio: ${u.socio}` : 'CCSLP'
          }))
      } catch (error) {
        console.error('Error fetching CCSLP users:', error)
        this.ccslpUsers = []
      } finally {
        this.loadingCCSLPUsers = false
      }
    },
    filterParticipants (item, queryText) {
      const searchText = queryText.toLowerCase()
      const fullName = (item.full_name || '').toLowerCase()
      const email = (item.email || '').toLowerCase()
      return fullName.includes(searchText) || email.includes(searchText)
    },
    resetParticipantData () {
      this.selectedParticipant = null
      this.participantSearch = null
      this.form.guest_name = ''
      this.form.guest_email = ''
      this.form.guest_handicap = 0
    },
    async handleSubmit () {
      const formRef = this.$refs.form
      if (!formRef.validate()) return

      const data = {}

      if (this.form.type === 'user' && this.selectedParticipant) {
        if (this.selectedParticipant.type === 'ccslp') {
          data.ccslp_user_id = this.selectedParticipant.id
        } else {
          data.user_id = this.selectedParticipant.id
        }
      } else if (this.form.type === 'guest') {
        data.guest_name = this.form.guest_name
        data.guest_email = this.form.guest_email
        if (this.form.guest_handicap > 0) {
          data.guest_handicap = this.form.guest_handicap
        }
      }

      this.$emit('register', data)
    },
    formatDate (date) {
      return new Date(date).toLocaleDateString('es-ES', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
::v-deep .v-dialog__container {
  align-items: flex-start;
  padding-top: 20px;
}
</style>
